import { useEffect, useState, useCallback } from 'react'
import { batchesAPI, coursesAPI } from '@utils/api'
import AdminLayout from '@components/admin/AdminLayout'
import {
  Plus, Pencil, Trash2, Loader2, X, AlertCircle, CalendarDays,
  Users, Clock, MapPin, Monitor, ChevronLeft, ChevronRight
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const MODES = ['Online', 'Offline', 'Hybrid']
const STATUSES = ['upcoming', 'ongoing', 'completed', 'cancelled']
const CURRENCIES = ['INR', 'USD', 'GBP', 'EUR', 'AED']

const statusColors = {
  upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  ongoing: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

const empty = {
  name: '', courseId: '', startDate: '', endDate: '', duration: '',
  mode: 'Online', schedule: '', totalSeats: 30, availableSeats: 30,
  price: '', discountedPrice: '', currency: 'INR',
  instructor: { name: '', email: '', phone: '', experience: '' },
  location: '', meetingLink: '', status: 'upcoming',
  support: { email: '', phone: '', whatsapp: '', availableHours: '' }
}

const fi = 'w-full px-3.5 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all'

const F = ({ label, hint, children, span }) => (
  <div className={span ? 'col-span-2' : ''}>
    <div className="flex items-center justify-between mb-1.5">
      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{label}</label>
      {hint && <span className="text-xs text-gray-400 dark:text-gray-500">{hint}</span>}
    </div>
    {children}
  </div>
)

const Sec = ({ children }) => (
  <div className="col-span-2 flex items-center gap-3 pt-2">
    <span className="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">{children}</span>
    <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
  </div>
)

const BatchModal = ({ batch, courses, onClose, onSaved }) => {
  const [form, setForm] = useState(batch ? {
    ...batch,
    startDate: batch.startDate?.slice(0, 10) || '',
    endDate: batch.endDate?.slice(0, 10) || '',
    courseId: batch.courseId?._id || batch.courseId || '',
    discountedPrice: batch.discountedPrice || '',
  } : { ...empty })
  const [saving, setSaving] = useState(false)

  const set = (path, val) => setForm(prev => {
    const next = { ...prev }
    const keys = path.split('.')
    let obj = next
    keys.slice(0, -1).forEach(k => { obj[k] = { ...obj[k] }; obj = obj[k] })
    obj[keys[keys.length - 1]] = val
    return next
  })

  const save = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        ...form,
        totalSeats: Number(form.totalSeats),
        availableSeats: Number(form.availableSeats),
        price: Number(form.price),
        discountedPrice: form.discountedPrice ? Number(form.discountedPrice) : undefined,
      }
      if (batch) await batchesAPI.update(batch._id, payload)
      else await batchesAPI.create(payload)
      toast.success(batch ? 'Batch updated' : 'Batch created')
      onSaved()
    } catch (err) {
      toast.error(err.message || 'Save failed')
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl my-6 border border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <CalendarDays className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">{batch ? 'Edit Batch' : 'New Batch'}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{batch ? `Editing: ${batch.name}` : 'Fill in the batch details below'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-400 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={save} className="p-6">
          <div className="grid grid-cols-2 gap-x-5 gap-y-4">

            <Sec>Batch Details</Sec>

            <F label="Batch Name" span>
              <input required value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. DevOps Batch #12" className={fi} />
            </F>

            <F label="Course" span>
              <select required value={form.courseId} onChange={e => set('courseId', e.target.value)} className={fi}>
                <option value="">Select a course</option>
                {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
              </select>
            </F>

            <F label="Start Date">
              <input required type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} className={fi} />
            </F>

            <F label="End Date">
              <input required type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} className={fi} />
            </F>

            <F label="Duration">
              <input required value={form.duration} onChange={e => set('duration', e.target.value)}
                placeholder="e.g. 3 months" className={fi} />
            </F>

            <F label="Schedule">
              <input required value={form.schedule} onChange={e => set('schedule', e.target.value)}
                placeholder="e.g. Weekdays 7–9 PM" className={fi} />
            </F>

            <F label="Mode">
              <select value={form.mode} onChange={e => set('mode', e.target.value)} className={fi}>
                {MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </F>

            <F label="Status">
              <select value={form.status} onChange={e => set('status', e.target.value)} className={fi}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </F>

            {(form.mode === 'Offline' || form.mode === 'Hybrid') && (
              <F label="Location" span>
                <input value={form.location} onChange={e => set('location', e.target.value)}
                  placeholder="City / Address" className={fi} />
              </F>
            )}
            {(form.mode === 'Online' || form.mode === 'Hybrid') && (
              <F label="Meeting Link" span>
                <input value={form.meetingLink} onChange={e => set('meetingLink', e.target.value)}
                  placeholder="https://zoom.us/j/..." className={fi} />
              </F>
            )}

            <Sec>Seats & Pricing</Sec>

            <F label="Total Seats">
              <input required type="number" min="1" value={form.totalSeats} onChange={e => set('totalSeats', e.target.value)} className={fi} />
            </F>

            <F label="Available Seats">
              <input required type="number" min="0" value={form.availableSeats} onChange={e => set('availableSeats', e.target.value)} className={fi} />
            </F>

            <F label="Currency">
              <select value={form.currency} onChange={e => set('currency', e.target.value)} className={fi}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>

            <F label="Price">
              <input required type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)}
                placeholder="0" className={fi} />
            </F>

            <F label="Discounted Price" hint="optional">
              <input type="number" min="0" value={form.discountedPrice} onChange={e => set('discountedPrice', e.target.value)}
                placeholder="0" className={fi} />
            </F>

            <Sec>Instructor</Sec>

            <F label="Name">
              <input required value={form.instructor.name} onChange={e => set('instructor.name', e.target.value)}
                placeholder="Instructor name" className={fi} />
            </F>

            <F label="Experience">
              <input value={form.instructor.experience} onChange={e => set('instructor.experience', e.target.value)}
                placeholder="e.g. 8 years" className={fi} />
            </F>

            <F label="Email">
              <input type="email" value={form.instructor.email} onChange={e => set('instructor.email', e.target.value)}
                placeholder="instructor@example.com" className={fi} />
            </F>

            <F label="Phone">
              <input value={form.instructor.phone} onChange={e => set('instructor.phone', e.target.value)}
                placeholder="+91 9876543210" className={fi} />
            </F>

            <Sec>Support Contact</Sec>

            <F label="Support Email">
              <input type="email" value={form.support.email} onChange={e => set('support.email', e.target.value)}
                placeholder="support@deoriginlabs.com" className={fi} />
            </F>

            <F label="Support Phone">
              <input value={form.support.phone} onChange={e => set('support.phone', e.target.value)}
                placeholder="+91 9876543210" className={fi} />
            </F>

            <F label="WhatsApp">
              <input value={form.support.whatsapp} onChange={e => set('support.whatsapp', e.target.value)}
                placeholder="+91 9876543210" className={fi} />
            </F>

            <F label="Available Hours">
              <input value={form.support.availableHours} onChange={e => set('support.availableHours', e.target.value)}
                placeholder="Mon–Fri 9AM–6PM" className={fi} />
            </F>

          </div>

          <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-gray-100 dark:border-gray-800">
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2 shadow-sm transition-all">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {batch ? 'Save Changes' : 'Create Batch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const AdminBatches = () => {
  const [batches, setBatches] = useState([])
  const [courses, setCourses] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterMode, setFilterMode] = useState('')
  const [page, setPage] = useState(1)
  const [modal, setModal] = useState(null)

  const fetchBatches = useCallback(async () => {
    setLoading(true)
    try {
      const res = await batchesAPI.getAll({
        page, limit: 10,
        status: filterStatus || undefined,
        mode: filterMode || undefined,
      })
      setBatches(res.data || [])
      setPagination(res.pagination || { page: 1, pages: 1, total: 0 })
    } catch { toast.error('Failed to load batches') }
    finally { setLoading(false) }
  }, [page, filterStatus, filterMode])

  useEffect(() => { fetchBatches() }, [fetchBatches])

  useEffect(() => {
    coursesAPI.getAll({ limit: 100, status: 'active' })
      .then(res => setCourses(res.data || []))
      .catch(() => {})
  }, [])

  const deleteBatch = async (id) => {
    if (!window.confirm('Delete this batch?')) return
    try {
      await batchesAPI.delete(id)
      toast.success('Batch deleted')
      fetchBatches()
    } catch { toast.error('Delete failed') }
  }

  return (
    <AdminLayout>
      {modal !== undefined && modal !== null && (
        <BatchModal
          batch={modal === 'new' ? null : modal}
          courses={courses}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchBatches() }}
        />
      )}

      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">Batches</h1>
            <p className="text-sm text-gray-500 mt-1">{pagination.total} total batches</p>
          </div>
          <button onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm">
            <Plus className="w-4 h-4" /> New Batch
          </button>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 flex flex-wrap gap-3">
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
            className="px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterMode} onChange={e => { setFilterMode(e.target.value); setPage(1) }}
            className="px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Modes</option>
            {MODES.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-3 flex items-center justify-center py-16">
              <Loader2 className="w-7 h-7 animate-spin text-primary-600" />
            </div>
          ) : batches.length === 0 ? (
            <div className="col-span-3 py-16 text-center">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No batches found</p>
            </div>
          ) : batches.map((batch) => (
            <div key={batch._id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-base leading-tight">{batch.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{batch.courseId?.title || 'Unknown Course'}</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ml-2 ${statusColors[batch.status]}`}>{batch.status}</span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <CalendarDays className="w-4 h-4 shrink-0 text-gray-400" />
                  <span>{batch.startDate ? format(new Date(batch.startDate), 'MMM d, yyyy') : '—'} → {batch.endDate ? format(new Date(batch.endDate), 'MMM d, yyyy') : '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Clock className="w-4 h-4 shrink-0 text-gray-400" />
                  <span>{batch.schedule}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  {batch.mode === 'Online' ? <Monitor className="w-4 h-4 shrink-0 text-gray-400" /> : <MapPin className="w-4 h-4 shrink-0 text-gray-400" />}
                  <span>{batch.mode}{batch.location ? ` · ${batch.location}` : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Users className="w-4 h-4 shrink-0 text-gray-400" />
                  <span>{batch.availableSeats} / {batch.totalSeats} seats available</span>
                </div>
              </div>

              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 mb-4">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all"
                  style={{ width: `${Math.min(((batch.totalSeats - batch.availableSeats) / (batch.totalSeats || 1)) * 100, 100)}%` }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="text-base font-bold text-gray-900 dark:text-white">
                    {batch.currency} {(batch.discountedPrice || batch.price)?.toLocaleString()}
                  </span>
                  {batch.discountedPrice && (
                    <span className="text-xs text-gray-400 line-through ml-1.5">{batch.currency} {batch.price?.toLocaleString()}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setModal(batch)} className="p-2 rounded-xl text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteBatch(batch._id)} className="p-2 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminBatches
